import { ComponentType, FC, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import {
  Box,
  ButtonBase,
  SvgIconProps,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import { UserContext } from '../..';
import { ROLE_ADMIN } from '../../constants';
import { clearSession } from '../../utils/session';
import { colors, fontWeights, radii } from '../../theme/tokens';
import {
  DocumentAddIcon,
  PeopleListIcon,
  PersonAddIcon,
  SearchDotsIcon,
} from './icons';

// The four XD navigation tiles ("Common structure" in DESIGN.md).
// They replace the sidebar: there is no other menu.
//
// The artboards draw them as a compact row centred in the page, each tile a
// rounded square with the icon centred on top and the label centred under it
// inside the tile: blue label on the light grey inactive tile, everything
// white on the solid blue active one.
//
// Icons: the XD glyphs, redrawn as project SVGs (see ./icons).
//
// Mobile (smartphone XD, S2): square tiles in two rows of three, and two
// extra tiles — "Mon compte" and "Se déconnecter" — that replace the header
// links. Kept compact (LTU's feedback on the mobile Home) so the document
// list fits below with less scrolling.

type Tile = {
  label: string;
  to: string;
  icon: ComponentType<SvgIconProps>;
  /** Route prefixes that mark the tile as active. */
  matches: string[];
  adminOnly?: boolean;
  /** Only in the mobile grid. */
  mobileOnly?: boolean;
  /** A button instead of a link: "Se déconnecter". */
  action?: 'logout';
};

const TILES: Tile[] = [
  {
    label: 'Ajouter un document',
    to: '/archive',
    icon: DocumentAddIcon,
    matches: ['/archive', '/archive-secured', '/archive-unsecured'],
  },
  {
    // The "Chercher un document" modal (screen 6) over the Home, and the
    // results screen (7) that comes out of it. The full listing stays
    // at "/all-documents", without a tile: it is the Home with "Archiveur" on "Tous".
    label: 'Recherche détaillée',
    to: '/search',
    icon: SearchDotsIcon,
    matches: ['/search'],
  },
  {
    label: 'Ajouter un utilisateur',
    to: '/create-user',
    icon: PersonAddIcon,
    matches: ['/create-user'],
    adminOnly: true,
  },
  {
    label: 'Liste des utilisateurs',
    to: '/users-list',
    icon: PeopleListIcon,
    matches: ['/users-list', '/edit-user', '/view-user'],
    adminOnly: true,
  },
  {
    label: 'Mon compte',
    to: '/profile',
    icon: ManageAccountsOutlinedIcon,
    matches: ['/profile'],
    mobileOnly: true,
  },
  {
    label: 'Se déconnecter',
    to: '/login',
    icon: LogoutOutlinedIcon,
    matches: [],
    mobileOnly: true,
    action: 'logout',
  },
];

const isActive = (tile: Tile, pathname: string) =>
  tile.matches.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );

const NavTiles: FC = () => {
  const userContext = useContext(UserContext);
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  const isAdmin = userContext?.mainUser?.role === ROLE_ADMIN;

  const tiles = TILES.filter(
    (tile) => (!tile.adminOnly || isAdmin) && (!tile.mobileOnly || isMobile),
  );

  const signOut = () => {
    clearSession();
    userContext?.updateUser(null);
    navigate('/login');
  };

  return (
    <Box
      component="nav"
      aria-label="Navigation principale"
      sx={(theme) => ({
        // XD: a compact row centred in the page, not a full-width band.
        // 2×2 grid on mobile (ROADMAP, Part 2), still centred.
        display: 'grid',
        gap: 1.5,
        justifyContent: 'center',
        gridTemplateColumns: 'repeat(4, 148px)',
        [theme.breakpoints.down('tablet')]: {
          gap: 0.75,
          gridTemplateColumns: 'repeat(3, minmax(0, 104px))',
        },
      })}
    >
      {tiles.map((tile) => {
        const active = isActive(tile, pathname);
        const Icon = tile.icon;

        return (
          <ButtonBase
            key={tile.to}
            // A router link, except "Se déconnecter", which is a button. The
            // Recherche modal returns to the route it was opened from
            // (unless that was already itself, or it would reopen).
            {...((tile.action === 'logout'
              ? { onClick: signOut }
              : {
                  component: Link,
                  to: tile.to,
                  state:
                    tile.to === '/search' && !pathname.startsWith('/search')
                      ? { from: `${pathname}${search}` }
                      : undefined,
                }) as object)}
            aria-current={active ? 'page' : undefined}
            focusRipple
            sx={(theme) => ({
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 1.25,
              minHeight: 124,
              p: 2,
              borderRadius: `${radii.card}px`,
              // XD: inactive tiles carry a border darker than their fill;
              // the active one keeps a same-colour border so both sizes match.
              border: `1px solid ${active ? colors.primary : colors.border}`,
              textAlign: 'center',
              transition: theme.transitions.create(
                ['background-color', 'color', 'box-shadow', 'border-color'],
                { duration: theme.transitions.duration.shorter },
              ),
              backgroundColor: active ? colors.primary : colors.surface,
              color: active ? colors.white : colors.primary,
              // The artboard glyph spans a good third of the tile's width.
              '& .NavTile-icon': {
                fontSize: 52,
                color: active ? colors.white : colors.accent,
              },
              '&:hover': {
                backgroundColor: active ? colors.primary : colors.border,
              },
              '&.Mui-focusVisible': {
                boxShadow: `0 0 0 3px ${colors.accentLight}`,
              },
              [theme.breakpoints.down('tablet')]: {
                gap: 0.5,
                minHeight: 0,
                aspectRatio: '1 / 1',
                justifyContent: 'center',
                p: 0.75,
                '& .NavTile-icon': { fontSize: 28 },
              },
            })}
          >
            <Icon className="NavTile-icon" />
            <Typography
              component="span"
              sx={(theme) => ({
                fontSize: '0.875rem',
                lineHeight: 1.35,
                fontWeight: fontWeights.medium,
                color: 'inherit',
                [theme.breakpoints.down('tablet')]: {
                  fontSize: '0.6875rem',
                  lineHeight: 1.25,
                },
              })}
            >
              {tile.label}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
};

export default NavTiles;
